import { useCallback, useSyncExternalStore } from 'react';
import type { HourFormat } from '../utils/locale';

const STORAGE_KEY = 'hourFormat';

function readStoredHourFormat(): HourFormat {
  try {
    if (typeof localStorage?.getItem !== 'function') return '24h';
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === '12h' ? '12h' : '24h';
  } catch {
    return '24h';
  }
}

let hourFormatSnapshot = readStoredHourFormat();
const listeners = new Set<() => void>();

function emitChange() {
  listeners.forEach(listener => listener());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): HourFormat {
  return hourFormatSnapshot;
}

function setStoredHourFormat(format: HourFormat) {
  if (format === hourFormatSnapshot) return;
  hourFormatSnapshot = format;
  try {
    localStorage.setItem(STORAGE_KEY, format);
  } catch {
    /* ignore */
  }
  emitChange();
}

if (typeof window !== 'undefined') {
  window.addEventListener('storage', event => {
    if (event.key !== STORAGE_KEY) return;
    const next = event.newValue === '12h' ? '12h' : '24h';
    if (next === hourFormatSnapshot) return;
    hourFormatSnapshot = next;
    emitChange();
  });
}

export function useHourFormat(): {
  hourFormat: HourFormat;
  setHourFormat: (format: HourFormat) => void;
} {
  const hourFormat = useSyncExternalStore(
    subscribe,
    getSnapshot,
    (): HourFormat => '24h'
  );
  const setHourFormat = useCallback((format: HourFormat) => setStoredHourFormat(format), []);

  return { hourFormat, setHourFormat };
}
